import React, { useContext, useState, useEffect } from 'react';
import styles from './BulkActions.module.scss';
import {
	Action,
	Trash,
	CheckmarkRoundSmall,
	Modal,
	CloseRoundSmall,
	TreeSelectOptions,
	Button,
	Checkbox,
} from 'esa-ui-library';
import { PropertyType, useStore } from '../../../../../common';
import {
	CHANGE_PERMISSIONS_MESSAGE,
	ADD_PERMISSIONS_TITLE,
	REMOVE_PERMISSIONS_TITLE,
	DELETE_ACCOUNTS_MESSAGE,
	DELETE_ACCOUNTS_TITLE,
	PERMISSIONS,
} from '../../../..';
import { AuthContext } from '../../../../../logic';
import { v4 as uuid } from 'uuid';
import { filter } from 'lodash';

const BulkActions = (): JSX.Element => {
	const [deleteModalOpened, setDeleteModalOpened] = useState(false);
	const [permAddModalOpened, setPermAddModalOpened] = useState(false);
	const [permRemoveModalOpened, setPermRemoveModalOpened] = useState(false);
	const [openedPartners, setOpenedPartners] = useState<string[]>([]);
	const [
		{ partners, activePartners, permissions, activePermissions, roles, activeRoles, currentPage, perPage },
		dispatch,
	] = useStore();
	const { user, token } = useContext(AuthContext);

	useEffect(() => {
		return () => dispatch('BULK_ACTIONS_INVALIDATE');
	}, []);

	const onAccountsDelete = async () => {
		await dispatch('DELETE_ACCOUNTS', token);
		dispatch('PAGE_UPDATE', { token, page: currentPage, perPage });
		setDeleteModalOpened(false);
	};

	const onAccountsActivate = async () => {
		await dispatch('ACTIVATE_ACCOUNTS', token);
		dispatch('PAGE_UPDATE', { token, page: currentPage, perPage });
	};

	const onAccountsDeactivate = async () => {
		await dispatch('DEACTIVATE_ACCOUNTS', token);
		dispatch('PAGE_UPDATE', { token, page: currentPage, perPage });
	};

	const onPartnersChange = (val: PropertyType[]) => {
		dispatch('PARTNERS_UPDATE', val);
	};

	const onPermissionsChange = (val: string) => {
		dispatch('PERMISSIONS_UPDATE', val);
	};

	const onRolesChange = (val: string) => {
		dispatch('ROLES_UPDATE', val);
	};

	const onSettingsAdd = async () => {
		await dispatch('SETTINGS_ADD', token);
		dispatch('PAGE_UPDATE', { token, page: currentPage, perPage });
	};

	const onSettingsRemove = async () => {
		await dispatch('SETTINGS_REMOVE', token);
		dispatch('PAGE_UPDATE', { token, page: currentPage, perPage });
	};

	return (
		<section className={styles.BulkActions}>
			{(user?.permissions?.isSuperAdmin || user?.permissions[PERMISSIONS.canChangeUserDetails]) && (
				<>
					{(user?.permissions?.isSuperAdmin || user?.permissions[PERMISSIONS.canChangeGroupMemberships]) && (
						<Action label='Change Partner Groups' dropdownClass={styles.GroupDropdown} className={styles.Action}>
							<>
								<TreeSelectOptions
									options={partners}
									selected={activePartners}
									opened={openedPartners}
									onChange={onPartnersChange}
									onOpenedChange={setOpenedPartners}
								/>
								<section className={styles.GroupActions} data-testid='group-actions'>
									<Button title='Remove' small className={styles.Action} onClick={onSettingsRemove} />
									<Button title='Add' small className={styles.Action} onClick={onSettingsAdd} />
								</section>
							</>
						</Action>
					)}
					<Action label='Change Permissions' dropdownClass={styles.GroupDropdown} className={styles.Action}>
						<>
							<section>
								{permissions.map((p: PropertyType) => (
									<Checkbox
										key={uuid()}
										label={p.label}
										checked={activePermissions.includes(p.value)}
										className={styles.Permission}
										onChange={() => onPermissionsChange(p.value)}
										dataId={p.name}
									/>
								))}
							</section>
							<section className={styles.GroupActions} data-testid='group-actions'>
								<Button title='Remove' small className={styles.Action} onClick={() => setPermRemoveModalOpened(true)} />
								<Button title='Add' small className={styles.Action} onClick={() => setPermAddModalOpened(true)} />
							</section>
						</>
					</Action>
					<Action label='Change Roles' dropdownClass={styles.GroupDropdown} className={styles.Action}>
						<>
							<section>
								{filter(roles, (r) => r.value !== 'SuperAdmin').map((r: PropertyType) => (
									<Checkbox
										key={uuid()}
										label={r.label}
										checked={activeRoles.includes(r.value)}
										className={styles.Role}
										onChange={() => onRolesChange(r.value)}
									/>
								))}
							</section>
							<section className={styles.GroupActions} data-testid='group-actions'>
								<Button title='Remove' small className={styles.Action} onClick={onSettingsRemove} />
								<Button title='Add' small className={styles.Action} onClick={onSettingsAdd} />
							</section>
						</>
					</Action>
				</>
			)}
			{(user?.permissions?.isSuperAdmin || user?.permissions[PERMISSIONS.canDeleteUser]) && (
				<Action className={styles.Action} label='Delete' onClick={() => setDeleteModalOpened(true)} icon={<Trash />} />
			)}
			{(user?.permissions?.isSuperAdmin || user?.permissions[PERMISSIONS.canDisableUser]) && (
				<>
					<Action
						className={styles.Action}
						label='Activate'
						onClick={onAccountsActivate}
						icon={<CheckmarkRoundSmall />}
					/>
					<Action
						className={styles.Action}
						label='Deactivate'
						onClick={onAccountsDeactivate}
						icon={<CloseRoundSmall />}
					/>
				</>
			)}
			{deleteModalOpened && (
				<Modal
					danger
					title={DELETE_ACCOUNTS_TITLE}
					submitLabel='Delete Accounts'
					onCancel={() => setDeleteModalOpened(false)}
					onSubmit={onAccountsDelete}
				>
					{DELETE_ACCOUNTS_MESSAGE}
				</Modal>
			)}
			{permAddModalOpened && (
				<Modal
					danger
					title={ADD_PERMISSIONS_TITLE}
					submitLabel='Add Permissions'
					onCancel={() => setPermAddModalOpened(false)}
					onSubmit={onSettingsAdd}
				>
					{CHANGE_PERMISSIONS_MESSAGE}
				</Modal>
			)}
			{permRemoveModalOpened && (
				<Modal
					danger
					title={REMOVE_PERMISSIONS_TITLE}
					submitLabel='Remove Permissions'
					onCancel={() => setPermRemoveModalOpened(false)}
					onSubmit={onSettingsRemove}
				>
					{CHANGE_PERMISSIONS_MESSAGE}
				</Modal>
			)}
		</section>
	);
};

export { BulkActions };
